import { is, type Nullable } from '@gov-nx/core/types';
import type { CommonSelectProps, FormRadioProps } from '@gov-nx/ui/types';
import { getRadioDefinition, getSelectDefinition } from '@gov-nx/utils/common';

export interface RezaChangeData {
	role: Nullable<'v_zastoupeni' | 'bez_zastoupeni'>;
	rezaCode: Nullable<string>;
}

export interface RezaChangeFields {
	role: FormRadioProps;
	rezaCode: CommonSelectProps;
}

interface useRezaChangeFormDefinitionProps {
	isRepresentationAllowed: boolean;
	options: { label: string; value: string }[];
	fields: {
		role: {
			label: string;
			required?: {
				value: boolean;
				message: string;
			};
			withoutRepresentationLabel: string;
			withRepresentationLabel: string;
			defaultValue?: RezaChangeData['role'];
		};
		rezaCode: {
			label: string;
			requiredMessage: string;
			selectPlaceholder?: string;
			defaultValue?: string;
		};
	};
}

export const useRezaChangeFormDefinition = ({
	isRepresentationAllowed,
	options,
	fields,
}: useRezaChangeFormDefinitionProps) => {
	const roleField = getRadioDefinition({
		name: 'role',
		label: fields.role.label,
		isRequired: fields.role.required?.value ?? false,
		isRequiredMessage: fields.role.required?.message ?? undefined,
		options: [
			{
				label: fields.role.withoutRepresentationLabel,
				name: 'bez_zastoupeni',
			},
			{
				label: fields.role.withRepresentationLabel,
				name: 'v_zastoupeni',
				disabled: !isRepresentationAllowed,
			},
		],
		defaultValue: fields.role.defaultValue ?? 'bez_zastoupeni',
	});

	const rezaCode = getSelectDefinition({
		name: 'rezaCode',
		label: fields.rezaCode.label,
		defaultValue: fields.rezaCode.defaultValue,
		isRequiredWhen: {
			fieldName: roleField.field.field.name,
			is: 'v_zastoupeni',
			errorMessage: fields.rezaCode.requiredMessage,
		},
		options: [
			fields.rezaCode.selectPlaceholder
				? { label: fields.rezaCode.selectPlaceholder, value: '' }
				: null,
			...options,
		].filter(is),
	});

	return [roleField, rezaCode];
};
